type NetworkListener = (online: boolean) => void;

const ONLINE_SETTLE_MS = 750;

/**
 * Single source of truth for browser connectivity.
 * Sleep/wake often drops the NIC (DNS fails with ERR_NAME_NOT_RESOLVED) while timers
 * keep firing auth, beacon, Twilio, and client-log requests → server 401 storms.
 *
 * Consumers should pause outbound API work while offline and resume on `online`.
 */
class NetworkAvailabilityService {
  private listeners = new Set<NetworkListener>();
  private online: boolean =
    typeof navigator !== 'undefined' ? navigator.onLine : true;
  private initialized = false;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private onceOnlineQueue: Array<() => void> = [];

  init = (): void => {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }
    this.initialized = true;
    this.online = navigator.onLine;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  };

  isOnline = (): boolean => {
    this.init();
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return false;
    }
    return this.online;
  };

  /**
   * Subscribe to connectivity changes. Immediately invoked with current state.
   * Returns unsubscribe.
   */
  subscribe = (listener: NetworkListener): (() => void) => {
    this.init();
    this.listeners.add(listener);
    try {
      listener(this.isOnline());
    } catch {
      // ignore listener errors
    }
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Run `fn` once when the network is considered usable again (or immediately if already online). */
  onceOnline = (fn: () => void): void => {
    this.init();
    if (this.isOnline()) {
      fn();
      return;
    }
    this.onceOnlineQueue.push(fn);
  };

  /**
   * True for DNS / NIC / fetch failures typical after sleep or airplane mode.
   * These should NOT trigger token refresh or be treated as auth failures.
   */
  isNetworkError = (error: unknown): boolean => {
    if (!this.isOnline()) {
      return true;
    }
    if (error == null) {
      return false;
    }

    const parts: string[] = [];
    if (typeof error === 'string') {
      parts.push(error);
    } else if (typeof error === 'object') {
      const err = error as Record<string, unknown>;
      if (typeof err.message === 'string') parts.push(err.message);
      if (typeof err.code === 'string') parts.push(err.code);
      if (typeof err.name === 'string') parts.push(err.name);
      const axiosMsg = (err as { response?: unknown }).response;
      if (!axiosMsg && typeof (err as { toJSON?: () => unknown }).toJSON === 'function') {
        try {
          parts.push(JSON.stringify((err as { toJSON: () => unknown }).toJSON()));
        } catch {
          // ignore
        }
      }
      // Axios: no response + Network Error
      if (err.isAxiosError && !err.response) {
        return true;
      }
    }

    const text = parts.join(' ');
    return /ERR_NAME_NOT_RESOLVED|ERR_INTERNET_DISCONNECTED|ERR_NETWORK_CHANGED|ERR_CONNECTION_|Failed to fetch|Network Error|net::ERR_|ECONNREFUSED|ENOTFOUND|ECONNRESET|ETIMEDOUT|networkerror|offline/i.test(
      text
    );
  };

  private handleOnline = (): void => {
    // After wake, DNS can still be flaky for a short window — settle before resuming.
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
    }
    this.settleTimer = setTimeout(() => {
      this.settleTimer = null;
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
      }
      this.setOnline(true);
    }, ONLINE_SETTLE_MS);
  };

  private handleOffline = (): void => {
    if (this.settleTimer) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
    this.setOnline(false);
  };

  private setOnline = (next: boolean): void => {
    if (this.online === next) {
      if (next) {
        this.flushOnceOnlineQueue();
      }
      return;
    }
    this.online = next;
    this.listeners.forEach((listener) => {
      try {
        listener(next);
      } catch {
        // ignore
      }
    });
    if (next) {
      this.flushOnceOnlineQueue();
    }
  };

  private flushOnceOnlineQueue = (): void => {
    const pending = this.onceOnlineQueue.splice(0, this.onceOnlineQueue.length);
    pending.forEach((fn) => {
      try {
        fn();
      } catch {
        // ignore
      }
    });
  };
}

export const NetworkAvailability = new NetworkAvailabilityService();
